import * as qs from "query-string";
import { API_URL } from "src/config";
import { getToken, removeToken, setToken } from "src/helpers";
import { fetchCurrentUser } from "src/modules/CurrentUser/redux/actions";
import store from "src/redux/configureStore";
import { getDefaultHeaders } from "./helpers";

// Will import mocks dynamically if required
const mockAPI = (
  (mockModule: null | typeof import("../server/mock/api")) => (): Promise<typeof import("../server/mock/api")> =>
    new Promise(resolve => {
      if (mockModule) {
        return resolve(mockModule);
      } else {
        import("../server/mock/api").then(importedModule => {
          mockModule = importedModule;
          return resolve(mockModule);
        });
      }
    })
)(null);

export interface IGetParams {
  [s: string]: string | number | boolean;
}

export interface IBody {
  [s: string]: any;
}

export interface IRequest {
  method: string;
  url: string;
  params?: IGetParams | null;
  body?: IBody | null | string;
  headers?: Record<string, string> | null;
  mock?: boolean;
}

interface IRefreshResponse {
  status: number;
  json: () => Promise<any>;
}
type ICallOptions = Omit<IRequest, "method">;
const defaultHeader = {
  Accept: "application/json",
  "Content-Type": "application/json"
};

async function fetchNewToken(token: string) {
  const options = {
    body: JSON.stringify({ code: token }),
    headers: {
      ...defaultHeader,
      Authorization: `Bearer ${token}`
    },
    method: "POST"
  };

  const response: IRefreshResponse = await fetch(`${API_URL}/v1/eagle-auth/refresh`, options);

  if (response.status >= 400) {
    // Remove invalid token if any
    removeToken();
    window.location.href = "/login";
    store.dispatch({ type: "FETCH_AUTH_TOKEN_REJECTED" });

    const error = { code: response.status };
    throw error;
  }

  const json = await response.json();

  setToken(json.token);
  store.dispatch(fetchCurrentUser());
}

let refreshing: null | Promise<any> = null;

export async function updateToken() {
  const refreshToken = getToken();
  if (!refreshToken) {
    window.location.href = "/login";
    store.dispatch({ type: "FETCH_AUTH_TOKEN_REJECTED" });

    const error = { code: 401 };
    throw error;
  }

  if (!refreshing) {
    refreshing = fetchNewToken(refreshToken);
  }

  await refreshing;

  refreshing = null;
}

const request = ({ method, url, params = null, body = null, headers = null, mock = false }: IRequest) =>
  ((defaultHeaders: Record<string, string>) =>
    mock
      ? mockAPI().then(({ mockRequest }) => mockRequest(method, url, params ? params : {}))
      : new Promise((resolve, reject) => {
          const payload = body && (body instanceof FormData ? body : JSON.stringify(body));
          return fetch(`${API_URL}${url}` + (params ? "?" + qs.stringify(params) : ""), {
            body: payload,
            headers: headers ? { ...defaultHeaders, ...headers } : { ...defaultHeaders },
            method
          })
            .then(resp => {
              const contentType = resp.headers.get("content-type");
              if (contentType && contentType.includes("application/octet-stream")) {
                return resolve(resp);
              }
              const resolver = (data: any) => {
                // Got unauthorized
                if (resp.status === 401) {
                  updateToken();
                } else {
                  return resp.ok ? resolve(data) : reject(data);
                }
              };

              if (contentType && contentType.includes("application/json")) {
                return resp.json().then(resolver);
              } else {
                return resp.text().then(resolver);
              }
            })
            .catch(error => reject(error));
        }))(
    body && !(body instanceof FormData)
      ? { ...getDefaultHeaders(), "Content-Type": "application/json" }
      : getDefaultHeaders()
  );

export const getCall = (options: ICallOptions) => {
  const { url, params, mock = false } = options;

  return request({
    method: "GET",
    mock,
    params,
    url
  });
};

export const deleteCall = (options: ICallOptions) => {
  const { url, params, mock = false } = options;
  return request({
    method: "DELETE",
    mock,
    params,
    url
  });
};
export const postCall = (options: ICallOptions) => {
  const { url, params, body, mock = false, headers } = options;
  return request({
    body,
    method: "POST",
    mock,
    params,
    url,
    headers
  });
};

export const putCall = (options: ICallOptions) => {
  const { url, params, body, mock = false } = options;
  return request({
    body,
    method: "PUT",
    mock,
    params,
    url
  });
};
export const patchCall = (options: ICallOptions) => {
  const { url, params, body, mock = false } = options;
  return request({
    body,
    method: "PATCH",
    mock,
    params,
    url,
    headers: {
      "Content-Type": "application/json-patch+json"
    }
  });
};
